<template>
  <div ref="contactFormSection" class="bg-wine-60 py-14 lg:pb-34 lg:pt-20">
    <div class="container">
      <h2
        v-if="data.title"
        class="mb-8 font-gt-ultra-fine text-5xl font-normal leading-[48px] text-off-white-10 lg:mb-24 lg:text-center lg:text-7xl lg:leading-[64px] lg:tracking-[-2px]"
      >
        {{ data.title }}
      </h2>

      <div class="flex justify-center">
        <div v-if="isSuccess" class="flex flex-col items-start px-9.5">
          <div
            class="bg-pink-light px-9.5 py-2.5 text-center font-medium text-button-primary-hover lg:px-25"
          >
            {{ $t("successQuestionCustomJewelry") }}
          </div>
        </div>

        <form
          v-else
          class="flex w-full max-w-[800px] flex-col items-start"
          @submit.prevent="onSubmit"
        >
          <h3
            v-if="data.subTitle"
            class="mb-7.5 text-[30px] font-medium leading-[1.2] text-text-dark"
          >
            {{ data.subTitle }}
          </h3>
          <div class="w-full space-y-5 lg:space-y-4">
            <div class="flex max-lg:flex-wrap max-lg:gap-y-5 lg:gap-x-4">
              <div class="w-full lg:w-1/2">
                <UiInput
                  v-model="contactForm.first_name"
                  class="flex-1"
                  name="first_name"
                  :autocomplete="'given-name'"
                  :placeholder="data.full_name"
                  size="md"
                  floating-label
                  :label="data.full_name"
                />
              </div>
              <div class="w-full lg:w-1/2">
                <UiInput
                  v-model="contactForm.last_name"
                  class="flex-1"
                  name="last_name"
                  :autocomplete="'family-name'"
                  :placeholder="data.last_name"
                  size="md"
                  floating-label
                  :label="data.last_name"
                />
              </div>
            </div>
            <div class="flex max-lg:flex-wrap max-lg:gap-y-5 lg:gap-x-4">
              <div class="w-full lg:w-1/2">
                <UiInput
                  v-model="contactForm.phone"
                  class="flex-1"
                  name="phone"
                  :autocomplete="'tel'"
                  :placeholder="$t('phoneNumberOptional')"
                  size="md"
                  floating-label
                  :label="$t('phoneNumberOptional')"
                />
              </div>
              <div class="w-full lg:w-1/2">
                <UiInput
                  v-model="contactForm.email"
                  class="flex-1"
                  name="email"
                  :autocomplete="'email'"
                  :placeholder="data.email"
                  size="md"
                  floating-label
                  :label="data.email"
                />
              </div>
            </div>
            <div class="w-full">
              <UiNativeSelect
                v-model="contactForm.design"
                class="w-full"
                name="design"
                :items="designList"
                title-key="name"
                :label="$t('whatWouldYouLikeToDesign')"
                floating-label
              />
            </div>
            <div class="w-full">
              <UiNativeSelect
                v-model="contactForm.when_finished_piece"
                class="w-full"
                name="when_finished_piece"
                :items="whenFinishedPieceList"
                title-key="name"
                :label="$t('whenDoYouNeedTheFinishedPieceBy')"
                floating-label
              />
            </div>
            <div class="w-full">
              <UiTextarea
                v-model="contactForm.question"
                class="flex-1"
                name="question"
                :placeholder="data.text_area"
                text-area-classes="resize-none h-[170px] lg:h-[190px]"
              />
            </div>
          </div>

          <UiButton
            :loading="isSubmitting"
            type="submit"
            color="secondary"
            class="mt-7 w-full uppercase lg:mt-10"
          >
            {{ data.button_text || $t("send") }}
          </UiButton>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { object, string } from "yup";
import { useForm } from "vee-validate";
import { QuestionFormModuleData, PageData, QuestionParams } from "ecom-types";

const { $api } = useNuxtApp();

const props = withDefaults(
  defineProps<{
    data: QuestionFormModuleData;
    blockIndex?: number;
    pageData: PageData | null;
  }>(),
  {
    blockIndex: -1,
  },
);

const route = useRoute();

const { openSystemDialog } = useSystemDialogs();

const { t } = useI18n();

const generalStore = useGeneralStore();

const { generateLeadEvent } = useEvents();

interface ContactForm {
  first_name: string;
  last_name: string;
  email: string;
  question: string;
  phone: string;
  subject: string;
  design: string;
  when_finished_piece: string;
}

const contactFormSection = ref<HTMLElement | null>(null);

const isSuccess = ref(false);

const designList = [
  { name: t("engagementRing"), value: t("engagementRing") },
  { name: t("somethingElse"), value: t("somethingElse") },
];

const whenFinishedPieceList = [
  { name: t("noSpecificDateButSoon"), value: t("noSpecificDateButSoon") },
  { name: t("lessThan30Days"), value: t("lessThan30Days") },
  { name: t("30-60Days"), value: t("30-60Days") },
  { name: t("notSure"), value: t("notSure") },
];

const initialContactForm: ContactForm = {
  first_name: "",
  last_name: "",
  email: "",
  question: "",
  phone: "",
  subject: t("customJewelryInquiry"),
  design: "",
  when_finished_piece: "",
};

const schema = object().shape({
  first_name: string().required().label(props.data.full_name),
  last_name: string().required().label(props.data.last_name),
  email: string().email().required().label(props.data.email),
  question: string().required().label(props.data.text_area),
  phone: string().label(t("phoneNumber")),
  subject: string().required().label(props.data.subject),
  design: string().required().label(t("whatWouldYouLikeToDesign")),
  when_finished_piece: string()
    .required()
    .label(t("whenDoYouNeedTheFinishedPieceBy")),
});

const contactForm = reactive(
  JSON.parse(JSON.stringify(initialContactForm)),
) as ContactForm;

const {
  handleSubmit: handleSubmitSignIn,
  resetForm,
  setErrors,
  isSubmitting,
} = useForm({
  initialValues: initialContactForm,
  validationSchema: schema,
});

const onSubmit = handleSubmitSignIn(async (values) => {
  try {
    const params: QuestionParams = {
      user_id: generalStore?.userInfo?.id || null,
      first_name: values.first_name,
      last_name: values.last_name,
      slug: window.location.href,
      phone: values.phone,
      question: `${t("whatWouldYouLikeToDesign")}: ${values.design}; ${t("whenDoYouNeedTheFinishedPieceBy")}: ${values.when_finished_piece}; ${values.question}`,
      email: values.email,
      subject: values.subject,
    };
    await $api.general.zenDeskContactUs(params);
    generateLeadEvent(`/${route.params.slug}`);
    resetForm();
    isSuccess.value = true;
    await nextTick();
    contactFormSection.value?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  } catch (error: any) {
    openSystemDialog({
      type: "alert",
      title: `${t("error")}!`,
      text: error?.response?._data?.errors?.[0]?.message || "Server error",
    });
    setErrors(responseErrorToObjectErrors(error?.response?._data?.errors));
  }
});
</script>
